import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * Tooltip Component - Info bubble with help text
 * Usage: <Tooltip text="Helpful explanation here" />
 */
export const Tooltip = ({ text, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };
  
  return (
    <div className="relative inline-block">
      <div
        className="cursor-help inline-flex items-center"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children || <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />}
      </div>
      
      {isVisible && (
        <>
          <div className={`absolute z-50 ${positionClasses[position]} w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg`}>
            <div className="relative">
              {text}
              {/* Arrow pointer */}
              <div 
                className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 ${
                  position === 'top' ? '-bottom-1 left-1/2 -translate-x-1/2' :
                  position === 'bottom' ? '-top-1 left-1/2 -translate-x-1/2' :
                  position === 'left' ? '-right-1 top-1/2 -translate-y-1/2' :
                  '-left-1 top-1/2 -translate-y-1/2'
                }`}
              />
            </div>
          </div>
          {/* Backdrop for mobile tap-away */}
          <div 
            className="fixed inset-0 z-40 md:hidden" 
            onClick={() => setIsVisible(false)}
          />
        </>
      )}
    </div>
  );
};

/**
 * LabelWithTooltip - Label with integrated tooltip
 * Usage: <LabelWithTooltip label="Opérateur" tooltip="Personne qui crée et gère l'opération" />
 */
export const LabelWithTooltip = ({ label, tooltip, required = false, children }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="font-medium text-sm text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      {tooltip && <Tooltip text={tooltip} />}
      {children}
    </div>
  );
};

export default Tooltip;
